// ABOUTME: Fetches the Animation Developer Specification JSON, transforms it into the §14 manifest
// ABOUTME: contract via buildManifest, validates, and HEAD-checks all media files at boot.
// ABOUTME: A bad artifact fails HERE with a visible error, never mid-demo.
// ABOUTME: The intro film (intro.mp4) is injected as the boot chapter ahead of the V004 chapters.

import { useEffect, useMemo, useState } from 'react';
import { validateManifest, mediaUrl } from './reelSchema';
import { buildManifest } from './reelSpecTransform';
import {
  buildCarouselChapters,
  extractEmbeddedVariantSections,
  isVariantChapterId,
} from './carouselSpecTransform';
import { resolveComposition } from './useViewportSize';
import { getDeviceProfileSnapshot } from '../../utils/deviceProfile';
import { mobileMediaFailed } from './mobileMediaFallback';

// Where the reel spec + chapter media load from. Three layers, later wins:
//   1. bundled defaults (public/reel/)
//   2. REACT_APP_* build-time env (per-build overrides; see .env.example)
//   3. GET /api/config → reel_spec_url / reel_media_base — per-DEPLOYMENT config, the way a new
//      reel cut is promoted with a backend configmap/.env change and NO frontend rebuild.
// App.js applies layer 3 via configureReelSources() the moment the config response lands —
// before preloadReelAssets() runs and long before the Projectionist mounts and fetches the spec.
// Unlike <video> media, the spec URL is fetch()ed, so a cross-origin host must allow GET from
// the app origin in its CORS rules — the intro container already does.
const REEL_SOURCE = {
  specUrl: process.env.REACT_APP_REEL_SPEC_URL || '/reel/S001_PB_V004.json',
  mediaBase: process.env.REACT_APP_REEL_MEDIA_BASE || '',
  // '' = fall back to INTRO_VIDEO_URL below (declared later in the file, so resolved at use
  // sites rather than here). Backend /api/config's intro_video lands here via App.js.
  introUrl: '',
  // SPEC-34: the carousel's own spec json (segment map for the variant footage). '' = the
  // feature is unconfigured and the manifest builds without variant chapters (E25). Lands
  // here from /api/config's carousel_spec_url, same route as specUrl.
  carouselSpecUrl: process.env.REACT_APP_CAROUSEL_SPEC_URL || '',
};

export function configureReelSources({ specUrl, mediaBase, introUrl, carouselSpecUrl } = {}) {
  if (specUrl) REEL_SOURCE.specUrl = specUrl;
  if (mediaBase) REEL_SOURCE.mediaBase = mediaBase;
  if (introUrl) REEL_SOURCE.introUrl = introUrl;
  if (carouselSpecUrl) REEL_SOURCE.carouselSpecUrl = carouselSpecUrl;
}

// SPEC-61 D-1 — the mobile pair is its own source triple, chosen once per manifest load, never
// a per-tenant branch: DIM/RJJ brand-agnosticism means the URLs are deployment DATA (an explicit
// REACT_APP_MOBILE_* override each, or else DERIVED from whatever REEL_SOURCE currently is —
// bundled default, build-time env, or the /api/config payload configureReelSources() just
// applied). No `/api/config` key is invented for the mobile triple itself (§9 of the spec) —
// but a hardcoded mobile default must never outrank a backend-configured desktop source, or
// promoting a new cut (or running a second reel_mode tenant) silently skews mobile only
// (review finding, SPEC-61). deriveMobileSpecUrl() applies the SAME matched-pair '_mobile'
// suffix rule pairedMediaName() uses for media filenames, just on the spec URL's full path
// (not only its basename) so the derived spec stays on the same host/directory as the active
// desktop one.
function deriveMobileSpecUrl(desktopSpecUrl) {
  const raw = String(desktopSpecUrl || '');
  const hashIdx = raw.indexOf('#');
  const withoutHash = hashIdx === -1 ? raw : raw.slice(0, hashIdx);
  const hash = hashIdx === -1 ? '' : raw.slice(hashIdx);
  const qIdx = withoutHash.indexOf('?');
  const path = qIdx === -1 ? withoutHash : withoutHash.slice(0, qIdx);
  const query = qIdx === -1 ? '' : withoutHash.slice(qIdx);
  return /\.json$/i.test(path) ? `${path.replace(/\.json$/i, '_mobile.json')}${query}${hash}` : null;
}

// The intro film follows the SAME matched-pair rule as the spec (2026-09-16). SPEC-61 shipped the
// mobile spec and reel media paired but left the film on the desktop cut, because no mobile film
// existed yet; the portrait one (1080x1920, 1673f) has since been promoted, and deriving it keeps
// the property that matters — a backend-promoted intro carries to mobile with no code or
// /api/config change, and a hardcoded mobile default can never outrank a configured desktop one.
//
// Derived off the path, not the basename, so the pair stays in the same container; `_current.mp4`
// therefore resolves `_current_mobile.mp4`. Null for anything that is not an .mp4 (a future intro
// served from a manifest/URL shape we have not seen), which the caller reads as "no mobile film".
function deriveMobileIntroUrl(desktopIntroUrl) {
  const raw = String(desktopIntroUrl || '');
  const hashIdx = raw.indexOf('#');
  const withoutHash = hashIdx === -1 ? raw : raw.slice(0, hashIdx);
  const hash = hashIdx === -1 ? '' : raw.slice(hashIdx);
  const qIdx = withoutHash.indexOf('?');
  const path = qIdx === -1 ? withoutHash : withoutHash.slice(0, qIdx);
  const query = qIdx === -1 ? '' : withoutHash.slice(qIdx);
  return /\.mp4$/i.test(path) ? `${path.replace(/\.mp4$/i, '_mobile.mp4')}${query}${hash}` : null;
}

// The reel media filename is NOT derived here: pairedMediaName() (below) already derives
// S001_PB_V004_mobile.mp4 from the spec basename — the matched-pair rule holds on mobile too,
// off whichever specUrl activeMobileSource() below actually resolves.
function activeMobileSource() {
  const derivedSpecUrl = deriveMobileSpecUrl(REEL_SOURCE.specUrl);
  return {
    specUrl: process.env.REACT_APP_MOBILE_REEL_SPEC_URL || derivedSpecUrl || REEL_SOURCE.specUrl,
    // The mobile file lives beside the desktop one under the same media base by construction
    // (matched-pair rule) — no separate default to invent, only an explicit escape hatch.
    mediaBase: process.env.REACT_APP_MOBILE_REEL_MEDIA_BASE || REEL_SOURCE.mediaBase,
    // Same three layers as the spec, and for the same reason: an explicit build-time override
    // first, else the `_mobile` pair of whatever film is actually configured (backend
    // `intro_video`, build-time env, or the bundled INTRO_VIDEO_URL default), else the desktop
    // film unchanged. No /api/config key is invented for it (§9) — deriving is what makes a
    // promoted cut carry to mobile by itself.
    introUrl: process.env.REACT_APP_MOBILE_INTRO_VIDEO_URL
      || deriveMobileIntroUrl(REEL_SOURCE.introUrl || INTRO_VIDEO_URL)
      || REEL_SOURCE.introUrl,
  };
}

// SPEC-61 D-2 — the device is resolved by the SAME pure rule useBreakpoint() uses
// (resolveComposition + the live device-profile snapshot), not a new detector. Read directly
// off window/document rather than through useViewportSize's hook store, because this file
// intentionally stays outside that module's edited surface (see the spec's module table) and
// `preloadReelAssets()` below is a plain function called with no React tree mounted yet.
function readWindowSize() {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  const doc = typeof document !== 'undefined' ? document.documentElement : null;
  return {
    width: (doc && doc.clientWidth) || window.innerWidth || 0,
    height: (doc && doc.clientHeight) || window.innerHeight || 0,
  };
}

export function isMobileReelComposition() {
  return resolveComposition(readWindowSize(), getDeviceProfileSnapshot()) === 'mobile';
}

/**
 * The source triple `preloadReelAssets()` and `load()` agree on, for THIS device. A phone whose
 * portrait pair has already failed this session gets the desktop pair (mobileMediaFallback.js).
 */
export function activeReelSource() {
  return isMobileReelComposition() && !mobileMediaFailed() ? activeMobileSource() : REEL_SOURCE;
}

// Test-only: module state survives across tests in a file (jest caches modules) — same pattern
// as useViewportSize's __resetDeviceProfileForTests. Only REEL_SOURCE is mutable module state;
// the mobile triple is now derived fresh on every call (activeMobileSource()), so there is
// nothing mobile-specific left to reset.
export function __resetReelSourcesForTests() {
  REEL_SOURCE.specUrl = process.env.REACT_APP_REEL_SPEC_URL || '/reel/S001_PB_V004.json';
  REEL_SOURCE.mediaBase = process.env.REACT_APP_REEL_MEDIA_BASE || '';
  REEL_SOURCE.introUrl = '';
  REEL_SOURCE.carouselSpecUrl = process.env.REACT_APP_CAROUSEL_SPEC_URL || '';
}

// The final intro film is ~190MB — too large to bundle in the app build — so it's hosted
// externally (Azure Blob Storage) and streamed straight from there instead of shipping
// under public/reel/ like the other chapter media. Caching is handled entirely by the
// browser's native HTTP cache via a Cache-Control header on the blob itself (works
// identically on every browser/device, unlike a hand-rolled Cache Storage layer, which
// Safari/iOS can silently evict or refuse to quota for a file this large).
//
// This points at a STABLE ALIAS blob name, not a versioned one (VersAI_LightField_Film_V012
// etc. are the immutable per-cut archive; this filename is copied-over each time a new cut
// is promoted). That split matters for caching: the alias must be re-checked periodically
// (Cache-Control: public, max-age=3600, must-revalidate on the blob) since its content
// changes over time, whereas an archived V0xx filename is content-immutable and can be
// cached forever. Never point this constant at a versioned filename — every future cut is
// then just "copy the new blob over this alias," zero code/config changes.
export const INTRO_VIDEO_URL =
  process.env.REACT_APP_INTRO_VIDEO_URL ||
  'https://mmdevukspublicstorage.blob.core.windows.net/ophion-images/VersAI_LightField_Film_current.mp4';

/**
 * Warms the manifest JSON and opens a connection to the intro film's host ahead of the
 * "Enter Boutique" click, so mounting the Projectionist afterwards doesn't race a cold
 * fetch + a fresh cross-origin DNS/TLS handshake. Call once, as soon as reel_mode is known
 * to be on (e.g. right after the brand config response lands) — not on every render.
 * Does NOT warm the Projectionist JS chunk itself; that's a separate `import()` call in
 * App.js against the exact same specifier React.lazy() uses, so the two share one chunk.
 */
export function preloadReelAssets() {
  // SPEC-61 D-2: warm the SAME triple load() will fetch — a mismatch here costs a cold fetch
  // (useChapterManifest.js's own note above `configureReelSources`).
  const active = activeReelSource();
  fetch(active.specUrl, { cache: 'force-cache' }).catch(() => {});
  // SPEC-34: warm the carousel spec the same way, so the boot-time merge fetch is a
  // cache hit rather than an extra cold round-trip. No-op when unconfigured. Desktop-only
  // (D-1) — the mobile pair embeds its variant plane in the combined spec, same as desktop's
  // combined cut, so it needs no second fetch.
  if (REEL_SOURCE.carouselSpecUrl) {
    fetch(REEL_SOURCE.carouselSpecUrl, { cache: 'force-cache' }).catch(() => {});
  }
  // A small ranged request is enough to establish the connection and warm the browser's
  // own disk cache entry; the full file streams in via range requests once playback starts.
  fetch(active.introUrl || INTRO_VIDEO_URL, { headers: { Range: 'bytes=0-65535' } }).catch(() => {});
}

// The intro film is an editorial chapter separate from the V004 animation spec.
// Its duration is a generous sentinel — the chapter ends via the video's natural `ended`
// event, not by the timeupdate boundary check. Safe for any intro shorter than 10 minutes.
const INTRO_SENTINEL_S = 600;

/**
 * The film the intro chapter will actually play, resolved once per manifest load.
 *
 * Desktop, and any build that names a mobile film explicitly, answer synchronously. A mobile
 * composition on a DERIVED url gets one HEAD first (2026-09-16): the `_mobile` pair is a naming
 * convention, not a guarantee, and an intro that 404s is a black screen on the first thing a guest
 * ever sees. A promoted cut whose portrait pair has not been uploaded yet therefore keeps playing
 * the desktop film — exactly the behaviour before the derive existed — instead of breaking.
 *
 * Not folded into the boot media check below: that check is a hard gate (a missing chapter file
 * fails the reel loudly, which is right for the animation), and this is deliberately the opposite
 * — a soft probe whose failure is a fallback. Cheap in practice, because `preloadReelAssets()` has
 * already opened the connection and warmed this exact url by the time the Projectionist mounts.
 */
async function resolveIntroMedia() {
  const desktop = REEL_SOURCE.introUrl || INTRO_VIDEO_URL;
  if (!isMobileReelComposition() || mobileMediaFailed()) return desktop;
  if (process.env.REACT_APP_MOBILE_INTRO_VIDEO_URL) return process.env.REACT_APP_MOBILE_INTRO_VIDEO_URL;
  const derived = deriveMobileIntroUrl(desktop);
  if (!derived || derived === desktop) return desktop;
  try {
    const res = await fetch(derived, { method: 'HEAD' });
    if (res.ok) return derived;
  } catch {
    // Network or CORS — indistinguishable from absent here, and both mean "do not risk it".
  }
  console.warn(
    `[SPEC-61] no portrait intro pair at ${derived} — the desktop film plays on mobile. `
    + 'Upload that blob (a copy of the current cut\'s mobile master) to fix.',
  );
  return desktop;
}

function makeIntroChapter(media) {
  return {
    id: 'intro',
    title: 'Vesper — Opening',
    variant: 'shared',
    // SPEC-61 D-1/D-2: the mobile pair's own intro film, on a mobile composition. Passed in
    // rather than read here, because resolving it on mobile is async (resolveIntroMedia above).
    media: media || activeReelSource().introUrl || INTRO_VIDEO_URL,
    // Only the intro film carries a baked-in audio track; every other chapter is silent
    // (the agent's voice comes over the WebSocket instead).
    audio: true,
    duration: INTRO_SENTINEL_S,
    segments: [{ type: 'move', start: 0, end: INTRO_SENTINEL_S }],
    beats: [],
    allowedTransitions: ['idle'],
  };
}

// Where a returning guest lands instead of the intro film: the variant/colour showcase
// (spec section CAROUSSEL → chapter id `collection`), which is the part guests actually browse
// the pieces in. `home` stays `idle` — "take me back" must still mean the hero hold.
const RETURNING_BOOT_CHAPTER = 'collection';

// SPEC-34: the lineup chapter the carousel routes through (transitionPlanner's `variantHome`).
// The same chapter the CAROUSSEL section maps to in reelSpecTransform.
const VARIANT_HOME_CHAPTER = 'collection';

// SPEC-34 D13: a spec .json and its .mp4 are a MATCHED PAIR — the media filename derives
// from the spec URL's basename and resolves against the same mediaBase as every other
// chapter. Renaming either blob means renaming both — deliberately. Extended to the MAIN
// pair with the combined cut (D26): promoting a V005 is then a pure REEL_SPEC_URL config
// change, no code names the file. Null when there is no .json basename to derive from —
// the caller keeps its own default (the transform's baked filename, for the main spec).
export function pairedMediaName(specUrl) {
  const base = String(specUrl || '').split('?')[0].split('#')[0].split('/').pop() || '';
  return /\.json$/i.test(base) ? base.replace(/\.json$/i, '.mp4') : null;
}

export function carouselMediaName(specUrl) {
  return pairedMediaName(specUrl) ?? 'Caroussel.mp4';
}

/**
 * SPEC-34: extend the main manifest with the carousel's variant chapters. Two modes,
 * decided by the ASSET, not by config (D26):
 *
 *   combined — the main spec embeds integer-id variant segments: build them against the
 *   MAIN media file (absolute frames, one fps by construction, no second fetch). The
 *   player never swaps sources between chapters that share a file, so slot entries are
 *   seeks — which is the whole point of the combined cut. A still-configured legacy
 *   carousel_spec_url is deliberately IGNORED (loud warn): merging split-cut footage over
 *   a combined film would double the variant plane with stale frames.
 *
 *   split — no embedded segments: the legacy second-pair fetch below, byte-identical.
 *
 * E25 — a missing/unreachable/malformed variant plane DEGRADES (loud console, manifest
 * unchanged, `show_variant` falls back to the range chapter backend-side): the main reel
 * must never fail to boot because the browse feature's asset is absent.
 * D11 — an fps mismatch on a PRESENT split spec is an authoring error and stays FATAL
 * (thrown with `.fatal`, surfacing through the red-panel path): silently mixing frame
 * cadences would corrupt every frame computation in the player. The combined shape cannot
 * mismatch — one spec, one cadence.
 */
async function extendWithVariants(v004, toolSpec, mainMedia) {
  const embedded = extractEmbeddedVariantSections(toolSpec);
  if (!embedded) return extendWithCarousel(v004);
  if (REEL_SOURCE.carouselSpecUrl) {
    console.warn(
      '[SPEC-34] the reel spec embeds its variant segments (combined cut) — ignoring the '
      + 'configured carousel_spec_url:', REEL_SOURCE.carouselSpecUrl,
    );
  }
  try {
    const chapters = buildCarouselChapters(embedded, {
      // The variants live in the main film — same media, same cadence. No HEAD probe
      // either: the main file is already vetted by the boot media check below.
      media: mainMedia ?? v004.chapters[0]?.media,
      fps: v004.fps,
    });
    // Dual-outro drop (2026-08-28): the spec splits the old CAROUSSEL span in two — the
    // exit is now the LEAVE (lineup collapses to neutral) and CAROUSSEL_APPROACH carries
    // the match-cut + dim-down INTO the slots. When that chapter exists the planner
    // routes slot entries through it and never plays the leave on the way in.
    const approach = v004.chapters.find((ch) => ch.id === 'collection_approach');
    // The relight strip (top-level `relight` in the spec) is the approach's mirror: the
    // dark lineup fading back to the lit hold — the authored way OUT of a slot.
    const relight = v004.chapters.find((ch) => ch.id === 'collection_relight');
    return {
      ...v004,
      chapters: [...v004.chapters, ...chapters],
      variantHome: VARIANT_HOME_CHAPTER,
      // D26: the combined cut's slot boundaries are the DARKENED lineup — the lineup
      // chapter's exit is the authored approach (dim-down), so the route planner must
      // play it rather than cut at the lit hold (transitionPlanner reads this key).
      // Kept even alongside approachChapter as the documented fallback for the
      // single-outro 2026-08-27 drop shape.
      variantApproach: 'exit',
      ...(approach ? { approachChapter: approach.id } : {}),
      ...(relight ? { relightChapter: relight.id } : {}),
    };
  } catch (err) {
    console.error(
      '[SPEC-34] embedded variant segments malformed — variant chapters disabled this '
      + 'session (the reel behaves as before SPEC-34):', err,
    );
    return v004;
  }
}

async function extendWithCarousel(v004) {
  const carouselSpecUrl = REEL_SOURCE.carouselSpecUrl;
  if (!carouselSpecUrl) return v004;
  try {
    const res = await fetch(carouselSpecUrl, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`${carouselSpecUrl} → HTTP ${res.status}`);
    const carouselSpec = await res.json();
    const carouselFps = carouselSpec.fps ?? 30;
    if (carouselFps !== v004.fps) {
      const err = new Error(
        `carousel spec fps ${carouselFps} != reel fps ${v004.fps} — the player's frame math `
        + 'is single-fps (SPEC-34 D11); fix the asset pair before shipping it',
      );
      err.fatal = true;
      throw err;
    }
    const chapters = buildCarouselChapters(carouselSpec, {
      media: carouselMediaName(carouselSpecUrl),
      fps: carouselFps,
    });
    const merged = { ...v004, chapters: [...v004.chapters, ...chapters], variantHome: VARIANT_HOME_CHAPTER };
    // Same dev-server HEAD check the main media gets (absolute URLs exempt, as ever) —
    // but a missing carousel FILE degrades rather than failing boot (E25).
    const probe = mediaUrl(merged, chapters[0]);
    if (!/^https?:/i.test(probe)) {
      const head = await fetch(probe, { method: 'HEAD' });
      const type = head.headers.get('content-type') || '';
      if (!head.ok || type.includes('text/html')) {
        throw new Error(`carousel media file not found: ${probe}`);
      }
    }
    return merged;
  } catch (err) {
    if (err.fatal) throw err;
    console.error(
      '[SPEC-34] carousel spec unavailable — variant chapters disabled this session '
      + '(the reel behaves as before SPEC-34):', err,
    );
    return v004;
  }
}

/**
 * Loads the reel manifest. Returns:
 *   status:   'loading' | 'ready' | 'error'
 *   errors:   string[] (when status === 'error')
 *   manifest: parsed manifest (when status === 'ready')
 *   getChapter: (id) => chapter | null
 *
 * @param {object} [options]
 * @param {boolean} [options.skipIntro] - Returning guest: boot straight into the collection
 *   chapter, resting there rather than handing off to home. The intro film chapter is STILL in
 *   the manifest (SPEC-43) so `replay_intro` can play it on request — it just is not boot.
 * @param {boolean} [options.skipToHome] - SPEC-82 skip_intro_film: a FIRST-TIME guest lands on the
 *   transform's own hero hold (boot = home = idle) with no film; the film chapter is still present
 *   for `replay_intro`. `skipIntro` wins when both are set (a returning guest keeps the carousel).
 */
function useChapterManifest({ skipIntro = false, skipToHome = false } = {}) {
  const [status, setStatus] = useState('loading');
  const [errors, setErrors] = useState([]);
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // SPEC-61 D-1/D-2: the composition is resolved ONCE per load and both the spec URL and
        // the media-base override below come from the same source triple — never a mix of the
        // two, and never re-resolved mid-session (deferred item 2 of the spec: a phone is
        // portrait-locked, and a desktop window narrowed below 768px does not earn mobile).
        const activeSource = activeReelSource();
        // Tells the Projectionist which films a phone can still fall back from (mobileMediaFallback).
        const mobileMedia = activeSource !== REEL_SOURCE;
        const specUrl = activeSource.specUrl;
        const res = await fetch(specUrl, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`${specUrl} → HTTP ${res.status}`);
        const toolSpec = await res.json();

        // Transform the Animation Developer Spec JSON → internal manifest schema (V004 chapters only).
        // Then prepend the intro film as the boot chapter; idle becomes home.
        let json;
        try {
          // D13/D26: the main pair is MATCHED too — derive the media filename from the spec
          // URL's basename so a new cut is promoted by config alone. The default spec URL
          // derives the same name the transform bakes in, so behaviour is unchanged there.
          const mainMedia = pairedMediaName(specUrl);
          let v004 = { ...buildManifest(toolSpec, {}, mainMedia ? { media: mainMedia } : {}) };
          // Deploy-time media base (backend /api/config, desktop-only per D-1) or the mobile
          // triple's own default wins over whatever the transform baked in at build time.
          // Shallow copy above — never mutate the transform's own object.
          if (activeSource.mediaBase) v004.mediaBase = activeSource.mediaBase;
          // SPEC-34: variant chapters — embedded in this spec (combined cut) or from the
          // configured second spec (split cut). Non-fatal by contract (E25) — only a split
          // spec's fps mismatch (D11) re-throws into this catch.
          v004 = await extendWithVariants(v004, toolSpec, mainMedia);
          // One resolution for both branches below — the returning guest's manifest still CARRIES
          // the intro chapter (off boot) so `replay_intro` works, so it needs the same film.
          const introMedia = await resolveIntroMedia();
          if (skipIntro) {
            // Returning guest: boot into the collection sweep and REST there
            // (bootHandoff === boot) instead of the intro's hand-off to home. SPEC-43: the
            // intro chapter is still PRESENT — off boot — so the guest can ask for the film
            // again (`reel_command` `replay_intro` → useIntroReplay) on every manifest; the
            // skip affordance and the player's escape hatch key off `audio: true`, not boot.
            // Falls back to the transform's own boot if a future cut drops the section —
            // a missing chapter must never take the reel down.
            // SPEC-61 D-7/F10: on a mobile manifest the returning guest boots straight into
            // the FROZEN roll (there is no picker without it), not the bare lineup — the same
            // key the route planner reads (transitionPlanner.js R1). Undefined `rollChapter`
            // (desktop, or a mobile pair whose jump target doesn't match) falls back to the
            // unchanged collection boot.
            const bootTarget = v004.rollChapter ?? RETURNING_BOOT_CHAPTER;
            const hasBootTarget = v004.chapters.some((ch) => ch.id === bootTarget);
            const chapters = [makeIntroChapter(introMedia), ...v004.chapters];
            json = hasBootTarget
              ? { ...v004, boot: bootTarget, bootHandoff: bootTarget, chapters }
              : { ...v004, chapters };
          } else if (skipToHome) {
            // SPEC-82: the existing fall-back shape above — film present (off boot), boot = the hero idle.
            json = { ...v004, chapters: [makeIntroChapter(introMedia), ...v004.chapters] };
          } else {
            const intro = makeIntroChapter(introMedia);
            json = { ...v004, boot: 'intro', chapters: [intro, ...v004.chapters] };
          }
        } catch (transformErr) {
          if (!cancelled) { setErrors([String(transformErr)]); setStatus('error'); }
          return;
        }
        json.mobileMedia = mobileMedia; // every branch above built `json` fresh, so this mutates nothing shared

        const { ok, errors: schemaErrors } = validateManifest(json);
        if (!ok) {
          if (!cancelled) { setErrors(schemaErrors); setStatus('error'); }
          return;
        }

        // Every bundled media file must exist before the first frame plays. The CRA dev
        // server answers unknown paths with index.html (HTTP 200), so a text/html
        // content-type is treated as "file missing". Cross-origin media (the externally
        // hosted intro film) is exempt: this check exists solely to catch that dev-server
        // fallback for files this app itself bundles, not to gate boot on a third-party
        // host's CORS configuration. A genuinely broken external URL still fails, just at
        // playback time — onVideoError already treats a boot-chapter load failure as an
        // intro-finished signal so the experience never hangs.
        // Variant-chapter media was already vetted (non-fatally) inside the variant merge
        // — re-checking it here would turn an E25 degrade case into a boot failure. But
        // only exempt files that are EXCLUSIVELY variant media: in combined mode the
        // variants share the MAIN film, and exempting it would silently drop the boot
        // existence check for the whole reel.
        const nonVariantMedia = new Set(
          json.chapters.filter((ch) => !isVariantChapterId(ch.id)).map((ch) => mediaUrl(json, ch)),
        );
        const variantMedia = new Set(
          json.chapters.filter((ch) => isVariantChapterId(ch.id))
            .map((ch) => mediaUrl(json, ch))
            .filter((u) => !nonVariantMedia.has(u)),
        );
        const urls = [...new Set(json.chapters.map((ch) => mediaUrl(json, ch)))]
          .filter((u) => !/^https?:/i.test(u))
          .filter((u) => !variantMedia.has(u));
        const missing = [];
        await Promise.all(urls.map(async (url) => {
          try {
            const head = await fetch(url, { method: 'HEAD' });
            const type = head.headers.get('content-type') || '';
            if (!head.ok || type.includes('text/html')) missing.push(url);
          } catch {
            missing.push(url);
          }
        }));
        if (missing.length > 0) {
          if (!cancelled) {
            setErrors(missing.map((u) => `media file not found: ${u}`));
            setStatus('error');
          }
          return;
        }

        if (!cancelled) { setManifest(json); setStatus('ready'); }
      } catch (e) {
        if (!cancelled) { setErrors([String(e)]); setStatus('error'); }
      }
    }

    load();
    return () => { cancelled = true; };
    // `skipIntro` is decided once (App.js reads it at mount, before the marker is written) and
    // the Projectionist only mounts after entry, so this never actually re-fires in practice —
    // it is in the deps for correctness, and the `cancelled` guard makes a re-run safe anyway.
  }, [skipIntro, skipToHome]);

  const getChapter = useMemo(() => {
    if (!manifest) return () => null;
    const byId = Object.fromEntries(manifest.chapters.map((ch) => [ch.id, ch]));
    return (id) => byId[id] ?? null;
  }, [manifest]);

  return { status, errors, manifest, getChapter };
}

export default useChapterManifest;
